<template>
  <!-- One-time install nudge, phones/tablets only (usePwaInstall gates that).
       Dismissal is remembered; installing or dismissing hides it for good. -->
  <div v-if="visible" class="install-banner">
    <Smartphone :size="20" class="install-banner-icon" aria-hidden="true" />
    <p class="install-banner-text">Add Everglance to your home screen for one-tap access.</p>
    <button type="button" class="install-banner-action" @click="onInstall">Install</button>
    <button type="button" class="install-banner-dismiss" aria-label="Dismiss" @click="dismiss">
      <X :size="16" />
    </button>

    <InstallAppSheet v-if="showSheet" @close="showSheet = false" />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { Smartphone, X } from 'lucide-vue-next'
import { usePwaInstall } from '@/composables/usePwaInstall'
import InstallAppSheet from './InstallAppSheet.vue'

const DISMISS_KEY = 'cmv_install_banner_dismissed'

const { showInstallUi, canPrompt, promptInstall } = usePwaInstall()

const dismissed = ref(false)
try {
  dismissed.value = localStorage.getItem(DISMISS_KEY) === '1'
} catch {
  /* private mode — banner simply reappears next session */
}

const showSheet = ref(false)
const visible = computed(() => showInstallUi.value && !dismissed.value)

async function onInstall() {
  if (canPrompt.value) {
    await promptInstall()
  } else {
    // iOS: no programmatic prompt exists — show the instructions sheet.
    showSheet.value = true
  }
}

function dismiss() {
  dismissed.value = true
  try {
    localStorage.setItem(DISMISS_KEY, '1')
  } catch {
    /* see above */
  }
}
</script>

<style scoped>
.install-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0.6rem 0 0;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--primary-color, #1b75d2);
  border-radius: 10px;
  background: var(--primary-bg-subtle, rgba(27, 117, 210, 0.08));
}

.install-banner-icon {
  flex-shrink: 0;
  color: var(--primary-color, #1b75d2);
}

.install-banner-text {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.35;
  color: var(--text-secondary, #374151);
}

.install-banner-action {
  flex-shrink: 0;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: var(--primary-color, #1b75d2);
  color: var(--text-inverse, #fff);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}

.install-banner-dismiss {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--text-muted, #6b7280);
  cursor: pointer;
}
</style>
